import { useEffect, useMemo, useState } from "react";
import { buildPatternsBySlug, type Exercise } from "@heraion/shared";

import { exerciseCatalog } from "./exercisesCache";

/** The exercise catalog for a web screen: best-effort hydration from the one
 *  `exerciseCatalog` source, null until it lands (a failed fetch leaves it
 *  as it is). `cachedFirst` paints the stored copy before the refresh.
 *  `patternsBySlug` is the movement pattern per exercise, `{}` meanwhile. */
export function useExerciseCatalog(opts: { cachedFirst?: boolean } = {}) {
  const cachedFirst = opts.cachedFirst === true;
  const [catalog, setCatalog] = useState<Exercise[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (cachedFirst) {
        const cached = await exerciseCatalog.loadCached();
        if (!cancelled && cached) setCatalog(cached);
      }
      try {
        const fresh = await exerciseCatalog.fetchAndPersist();
        if (!cancelled) setCatalog(fresh);
      } catch {
        // Best-effort refresh; keep what is shown.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cachedFirst]);
  const patternsBySlug = useMemo(
    () => (catalog ? buildPatternsBySlug(catalog) : {}),
    [catalog],
  );
  return { catalog, setCatalog, patternsBySlug };
}
